import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { formatCourtTagLabel } from "@bw/shared/utils";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import type { Court } from "@/libs/facilityApi";

const STATUS_COLOR: Record<
  Court["status"],
  "default" | "error" | "success" | "warning"
> = {
  enabled: "success",
  disabled: "default",
  maintenance: "warning",
  deleted: "error",
};

interface CourtInventoryPanelsProps {
  courts: Court[];
  loading: boolean;
  canManageCourts: boolean;
  onEdit: (court: Court) => void;
  onRetire: (court: Court) => void;
}

function InventoryMetric({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ alignItems: "baseline", whiteSpace: "nowrap" }}
    >
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function CourtInventoryPanels({
  courts,
  loading,
  canManageCourts,
  onEdit,
  onRetire,
}: CourtInventoryPanelsProps) {
  const theme = useTheme();
  // Leave room for the permanent navigation drawer as well as the table.
  const compact = useMediaQuery(theme.breakpoints.down("lg"));
  const enabled = courts.filter((court) => court.status === "enabled").length;
  const maintenance = courts.filter(
    (court) => court.status === "maintenance",
  ).length;
  const metricValue = (value: number): number | string =>
    loading ? "—" : value;

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 3 }}
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Court inventory
        </Typography>
        <Stack
          direction="row"
          spacing={{ xs: 2, sm: 3 }}
          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
        >
          <InventoryMetric label="Total" value={metricValue(courts.length)} />
          <InventoryMetric label="Enabled" value={metricValue(enabled)} />
          <InventoryMetric
            label="Maintenance"
            value={metricValue(maintenance)}
          />
        </Stack>
      </Stack>

      {compact ? (
        loading ? (
          <Stack
            role="status"
            aria-live="polite"
            direction="row"
            spacing={1}
            sx={{ p: 3, alignItems: "center", justifyContent: "center" }}
          >
            <CircularProgress size={18} />
            <Typography color="text.secondary" variant="body2">
              Loading…
            </Typography>
          </Stack>
        ) : courts.length === 0 ? (
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{ p: 3, textAlign: "center" }}
          >
            No courts yet.
          </Typography>
        ) : (
          <Stack
            component="ul"
            aria-label="Court inventory"
            sx={{ listStyle: "none", m: 0, p: 0 }}
          >
            {courts.map((court) => (
              <Stack
                component="li"
                key={court.court_id}
                spacing={1.5}
                sx={{
                  p: 2,
                  minWidth: 0,
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    component="h3"
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {court.name}
                  </Typography>
                  <Chip
                    color={STATUS_COLOR[court.status] ?? "default"}
                    label={court.status}
                    size="small"
                    variant={court.status === "enabled" ? "filled" : "outlined"}
                    sx={{ textTransform: "capitalize", flexShrink: 0 }}
                  />
                </Stack>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Capacity: {court.max_players ?? "Not set"}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    Tags:{" "}
                    {(court.tags ?? []).map(formatCourtTagLabel).join(", ") ||
                      "None"}
                  </Typography>
                </Stack>
                {canManageCourts ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      aria-label={`Edit ${court.name}`}
                      onClick={() => onEdit(court)}
                      startIcon={<EditRounded />}
                      variant="outlined"
                      sx={{ flex: 1, minWidth: 0, minHeight: 44 }}
                    >
                      Edit
                    </Button>
                    <Button
                      aria-label={`Retire ${court.name}`}
                      onClick={() => onRetire(court)}
                      startIcon={<DeleteOutlineRounded />}
                      color="error"
                      variant="outlined"
                      sx={{ flex: 1, minWidth: 0, minHeight: 44 }}
                    >
                      Retire
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            ))}
          </Stack>
        )
      ) : (
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table
            aria-label="Court inventory"
            size="small"
            sx={{ minWidth: 680 }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right">Capacity</TableCell>
                {canManageCourts ? (
                  <TableCell align="right">Actions</TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManageCourts ? 5 : 4} align="center">
                    <Stack
                      aria-live="polite"
                      direction="row"
                      role="status"
                      spacing={1}
                      sx={{
                        minHeight: 80,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size={18} />
                      <Typography color="text.secondary" variant="body2">
                        Loading…
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : courts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canManageCourts ? 5 : 4}
                    align="center"
                    sx={{ height: 96 }}
                  >
                    <Typography color="text.secondary" variant="body2">
                      No courts yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                courts.map((court) => (
                  <TableRow hover key={court.court_id}>
                    <TableCell sx={{ fontWeight: 600 }}>{court.name}</TableCell>
                    <TableCell>
                      <Chip
                        color={STATUS_COLOR[court.status] ?? "default"}
                        label={court.status}
                        size="small"
                        variant={
                          court.status === "enabled" ? "filled" : "outlined"
                        }
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {(court.tags ?? []).map(formatCourtTagLabel).join(", ") ||
                        "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "text.secondary" }}>
                      {court.max_players ?? "—"}
                    </TableCell>
                    {canManageCourts ? (
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ justifyContent: "flex-end" }}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label={`Edit ${court.name}`}
                              color="primary"
                              onClick={() => onEdit(court)}
                            >
                              <EditRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Retire">
                            <IconButton
                              aria-label={`Retire ${court.name}`}
                              color="error"
                              onClick={() => onRetire(court)}
                            >
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
